import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { isDmaConsultantUser } from '@upright/common/utils';
import { useMyAccessibleCompaniesQuery } from '@upright/ui/api/iam/organizations/hooks';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import { buildChatUrl, toChatEntityType } from './chat-url';
import { type CurrentPageChatBinding, useCurrentPageChatBinding } from './current-page-chat-binding';

/**
 * DMA Consultant chat is company-scoped (Janne, 2026-06-08, PLT-5041).
 *
 * A consultant user with access to MORE THAN ONE company may only use chat
 * "inside a company". A consultant with access to exactly ONE company
 * (typically a client-customer login) sees chat normally everywhere, as does
 * every other org type. Kept as a pure, exported helper so the gating rule is
 * expressed in one inspectable place, independent of the hook's wiring. (No
 * standalone unit test: this repo's mocha unit runner uses the backend
 * tsconfig, which has no `@upright/ui` path mappings, so a spec importing this
 * module wouldn't resolve — the rule is a one-line boolean exercised via the
 * component path.)
 */
export function shouldGateAskAiToCompany(isConsultant: boolean, accessibleCompanyCount: number): boolean {
  return isConsultant && accessibleCompanyCount > 1;
}

export interface AskAiEntryPointState {
  /**
   * Don't render the entry point at all. True when the caller lacks the
   * `USE_AI_CHAT_WEB` privilege (or it's still loading), or when a
   * multi-company DMA Consultant is off-company. The latter is option C
   * (PLT-5235): off-company the entry points are hidden entirely — not merely
   * disabled — so every chat a consultant can start carries a client company
   * and is metered to it.
   */
  hidden: boolean;
  /**
   * Live page-chat binding, or `null` off an entity page. Exposed for the
   * desktop top-bar opener, which reads the entityId / label at click time to
   * seed the floating panel.
   */
  binding: CurrentPageChatBinding | null;
  /**
   * True when the current page is bound to a client **company** specifically —
   * a fund or portfolio page is `false`. This exists for the consultant
   * metering gate (a turn has to be attributed to one client company); it is
   * not a general "does this page have an entity" test, which is
   * `binding !== null`. See the note on `href`.
   */
  insideCompany: boolean;
  /**
   * Full-page `/chat` URL to navigate to. On **any** entity page — company,
   * fund or portfolio — it carries that entity's context (`entityId` /
   * `entityType` / `entityLabel`) so the chat opens about it, and on a company
   * the turn is attributed to it as well (option C). Off an entity page it is
   * the bare `/chat?from=…`.
   */
  href: string;
}

/**
 * Shared state for every "Ask AI" entry point — the desktop top-bar link, the
 * DMA white-header link, and the mobile burger item. Centralises the
 * chat-privilege gate, the consultant off-company gate (option C: hidden, not
 * disabled), and the entity-context URL so the three call sites stay in
 * lock-step.
 *
 * `from` is the entry-point hint that drives only the `/chat` logo's
 * back-destination in `HeaderMaybe` (`'dma'` → `/dashboard`, `'public'` → `/`).
 */
export function useAskAiEntryPoint(from: 'public' | 'dma'): AskAiEntryPointState {
  const { hasPrivilege, loading } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_AI_CHAT_WEB);
  const user = useGetLoggedInUser();
  const isConsultant = isDmaConsultantUser(user);
  // Only consultants need the company count to decide gating — `enabled`
  // keeps every other user (the vast majority) from firing this query.
  const { data: accessibleCompanies } = useMyAccessibleCompaniesQuery({ enabled: isConsultant });
  const binding = useCurrentPageChatBinding();

  const accessibleCompanyCount = accessibleCompanies?.companies.length ?? 0;
  // Company-only on purpose, and only for the consultant gate on the next line: that gate
  // exists so a consultant's turn can be metered to one client company, so here "inside" really
  // does mean a company. Do not reuse it to decide whether the chat URL carries context — a
  // fund or portfolio binding is every bit as bindable, and conflating the two is what dropped
  // the fund from the href (PLT-6554).
  const insideCompany = binding?.entityType === 'company';
  // While the company count is still loading we treat the user as un-gated
  // (count 0 → not gated); the brief window before it resolves is no worse
  // than the always-enabled behaviour, and the backend re-enforces the
  // company-context rule regardless.
  const hidden =
    loading || !hasPrivilege || (shouldGateAskAiToCompany(isConsultant, accessibleCompanyCount) && !insideCompany);

  // Any published binding, whatever its type. A fund or portfolio page publishes a `group` /
  // `portfolio` binding (`CompanyGroupView` / `PortfolioView`), and the panel path has always
  // passed it through untouched — but this href was gated on `insideCompany`, so the paths that
  // use it and not the panel dropped the entity: cmd / ctrl / shift / middle click (which
  // `useOpenAskAiPanel` deliberately lets fall through to native navigation) and the mobile
  // burger item, which is href-only with no panel fallback at all. Both landed on a bare `/chat`
  // (PLT-6554). Nothing downstream needed widening for this: `chat-view` reads the params
  // without a type whitelist and the context chip already renders a group or a portfolio.
  //
  // The narrowing is a type formality: `buildChatUrl` takes the chat's entity types and the
  // published binding carries a plain string, but every publisher passes a company, a group or
  // a portfolio, so the bare-URL branch is not reached for a bound page today.
  const entityType = binding !== null ? toChatEntityType(binding.entityType) : null;
  const href =
    binding !== null && entityType !== null
      ? buildChatUrl({ from, entityId: binding.entityId, entityType, entityLabel: binding.label })
      : `/chat?from=${from}`;

  return { hidden, binding, insideCompany, href };
}
